import {
  signInWithCustomToken,
  type Auth,
  type UserCredential,
} from 'firebase/auth';

/**
 * S-07: tenant-portal が saas-admin から渡された impersonation Custom Token を
 * URL fragment (`#impersonate_token=...`) から取り出して `signInWithCustomToken` する。
 *
 * - fragment にしているのは HTTP リクエスト・Referer・Hosting access log への漏洩回避（cf. saas-admin/lib/impersonationUrl.ts）
 * - 消費後は `history.replaceState` で fragment を即座に剥がす（リロード時の二重消費を防ぐ）
 * - fragment が無ければ no-op
 *
 * 戻り値:
 *   - { kind: 'consumed', credential }  : 正常にサインイン済み
 *   - { kind: 'noop' }                 : fragment 無し
 *   - { kind: 'error', error }         : signInWithCustomToken 失敗（fragment は剥がす）
 *
 * テスト容易性のため `signInWithCustomToken` 実行関数と location/history を引数化する。
 */
export interface ImpersonationConsumeDeps {
  /** `Window.location` 互換。テストではモックを渡す */
  locationLike: { hash: string; pathname: string; search: string };
  /** `Window.history.replaceState` 互換 */
  replaceState: (state: unknown, title: string, url: string) => void;
  /** Firebase Auth インスタンス */
  auth: Auth;
  /** テスト用に差し替え可能な signInWithCustomToken */
  signIn?: (auth: Auth, token: string) => Promise<UserCredential>;
}

export type ImpersonationConsumeResult =
  | { kind: 'noop' }
  | { kind: 'consumed'; credential: UserCredential; tokenLength: number }
  | { kind: 'error'; error: unknown };

/** fragment から token を抽出する純関数。test しやすいように export。 */
export function extractImpersonationToken(hash: string): string | null {
  // hash は "#impersonate_token=..." または "#a=1&impersonate_token=..." の両形式を許容
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return null;
  for (const part of trimmed.split('&')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const key = part.slice(0, eq);
    if (key !== 'impersonate_token') continue;
    const raw = part.slice(eq + 1);
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      // デコード失敗は不正値として noop 扱い
      return null;
    }
  }
  return null;
}

/**
 * fragment を剥がした後の URL（pathname + search のみ）。
 * pathname が空文字なら "/" にフォールバック（history.replaceState の URL は空にできない）。
 */
export function stripImpersonationFragment(loc: {
  pathname: string;
  search: string;
}): string {
  return `${loc.pathname || '/'}${loc.search || ''}`;
}

export async function consumeImpersonationToken(
  deps: ImpersonationConsumeDeps,
): Promise<ImpersonationConsumeResult> {
  const token = extractImpersonationToken(deps.locationLike.hash);
  if (!token) return { kind: 'noop' };

  // どんな結果でも fragment は必ず剥がす（再消費・URL 共有時のリーク防止）
  const cleanUrl = stripImpersonationFragment(deps.locationLike);
  try {
    deps.replaceState(null, '', cleanUrl);
  } catch {
    // SSR 等で history が無い場合は無視
  }

  const signIn = deps.signIn ?? signInWithCustomToken;
  try {
    const credential = await signIn(deps.auth, token);
    return { kind: 'consumed', credential, tokenLength: token.length };
  } catch (error) {
    return { kind: 'error', error };
  }
}

/**
 * S-07b: impersonation consume 失敗の分類コード。
 *
 * - `network` : ネットワーク到達不能（`auth/network-request-failed`）
 * - `invalid` : Custom Token が不正または**期限切れ**。Firebase の
 *   `signInWithCustomToken` は token 形式不正と exp 切れをどちらも
 *   `auth/invalid-custom-token` で返し client では区別できないため統合する
 *   （cf. S-07b 判断点 / 受け入れ条件の expired と invalid を統合）
 * - `unknown` : 上記以外（`auth/internal-error` など）
 */
export type ImpersonationFailureCode = 'invalid' | 'network' | 'unknown';

/** Firebase AuthError 等の `code` を見て失敗を分類する純関数。test しやすいように export。 */
export function classifyImpersonationFailure(error: unknown): ImpersonationFailureCode {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : undefined;
  switch (code) {
    case 'auth/network-request-failed':
      return 'network';
    case 'auth/invalid-custom-token':
    case 'auth/custom-token-mismatch':
      return 'invalid';
    default:
      return 'unknown';
  }
}

/** 失敗コード → SignInPage 表示用の日本語メッセージ（社内ツールのため i18n 不要）。 */
export function impersonationFailureMessage(code: ImpersonationFailureCode): string {
  switch (code) {
    case 'invalid':
      return 'impersonation トークンが無効、または有効期限が切れています。saas-admin から再度開いてください。';
    case 'network':
      return 'ネットワークエラーで impersonation を開始できませんでした。接続を確認して saas-admin から再度お試しください。';
    default:
      return '原因を特定できませんでした。お手数ですが saas-admin から再度お試しください。';
  }
}
